<template>
  <div class="dashboard-panel">
    <!-- Skeleton -->
    <div v-if="!partners || partners.length === 0" class="kpi-empty">
      <div class="kpi-empty__icon">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect x="4" y="8" width="40" height="32" rx="4" stroke="#B8BCC4" stroke-width="2" fill="none" />
          <line x1="4" y1="16" x2="44" y2="16" stroke="#B8BCC4" stroke-width="2" />
          <line x1="16" y1="8" x2="16" y2="40" stroke="#E8EAED" stroke-width="1" />
          <line x1="28" y1="8" x2="28" y2="40" stroke="#E8EAED" stroke-width="1" />
        </svg>
      </div>
      <span class="kpi-empty__text">{{ t('dashboard.noData') }}</span>
    </div>

    <!-- PC: AG Grid Table -->
    <div v-else-if="isPcRedesign" class="kpi-grid-wrapper">
      <ag-grid-vue
        class="kpi-ag-grid"
        :columnDefs="columnDefs"
        :rowData="partners"
        :defaultColDef="defaultColDef"
        :domLayout="'autoHeight'"
        :suppressHorizontalScroll="false"
        :headerHeight="36"
        :rowHeight="44"
      />
    </div>

    <!-- Mobile: Card List -->
    <div v-else class="kpi-cards">
      <div v-for="(partner, idx) in partners" :key="idx" class="kpi-card">
        <div class="kpi-card__header">
          <span class="kpi-card__name">{{ partner.partnerName }}</span>
        </div>
        <div class="kpi-card__metrics">
          <div class="kpi-metric">
            <span class="kpi-metric__label">{{ t('dashboard.totalRequests') }}</span>
            <span class="kpi-metric__value kpi-metric__value--mono">{{ formatNumber(partner.totalRequests) }}</span>
          </div>
          <div class="kpi-metric">
            <span class="kpi-metric__label">{{ t('dashboard.completedRequests') }}</span>
            <span class="kpi-metric__value kpi-metric__value--mono">{{ formatNumber(partner.completedRequests) }}</span>
          </div>
          <div class="kpi-metric">
            <span class="kpi-metric__label">{{ t('dashboard.completionRate') }}</span>
            <span class="kpi-metric__value">
              <span :class="kpiBadgeClass(partner.completionRate, 85)">{{ formatPercent(partner.completionRate) }}</span>
            </span>
          </div>
          <div class="kpi-metric">
            <span class="kpi-metric__label">{{ t('dashboard.ftfr') }}</span>
            <span class="kpi-metric__value">
              <span :class="kpiBadgeClass(partner.ftfr, 80)">{{ formatPercent(partner.ftfr) }}</span>
            </span>
          </div>
          <div class="kpi-metric">
            <span class="kpi-metric__label">{{ t('dashboard.revisitRate') }}</span>
            <span class="kpi-metric__value">
              <span :class="kpiBadgeClassInverse(partner.revisitRate, 10)">{{ formatPercent(partner.revisitRate) }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps */
import { computed } from 'vue'
import i18n from '@/locales/i18n'
import { AgGridVue } from 'ag-grid-vue3'

const t = i18n.global.t

const props = defineProps({
  data: { type: Object, default: null },
  isPcRedesign: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false }
})

const partners = computed(() => props.data?.partners || [])

const defaultColDef = {
  cellDataType: false,
  sortable: true,
  resizable: true,
  suppressMovable: true
}

const columnDefs = computed(() => [
  {
    headerName: t('dashboard.partner'),
    field: 'partnerName',
    minWidth: 140,
    flex: 1,
    cellStyle: { fontWeight: '600' }
  },
  {
    headerName: t('dashboard.totalRequests'),
    field: 'totalRequests',
    width: 120,
    cellRenderer: (params) => {
      const val = params.value
      if (val == null) return '-'
      return `<span style="font-weight:600;font-variant-numeric:tabular-nums;">${Number(val).toLocaleString()}</span>`
    }
  },
  {
    headerName: t('dashboard.completedRequests'),
    field: 'completedRequests',
    width: 120,
    cellRenderer: (params) => {
      const val = params.value
      if (val == null) return '-'
      return `<span style="font-weight:600;font-variant-numeric:tabular-nums;">${Number(val).toLocaleString()}</span>`
    }
  },
  {
    headerName: t('dashboard.completionRate'),
    field: 'completionRate',
    width: 130,
    cellRenderer: (params) => renderPercentBadge(params.value, 85)
  },
  {
    headerName: t('dashboard.ftfr'),
    field: 'ftfr',
    width: 130,
    cellRenderer: (params) => renderPercentBadge(params.value, 80)
  },
  {
    headerName: t('dashboard.revisitRate'),
    field: 'revisitRate',
    width: 130,
    cellRenderer: (params) => renderPercentBadgeInverse(params.value, 10)
  }
])

function renderPercentBadge(value, threshold) {
  if (value == null) return '-'
  const v = Number(value)
  const color = v >= threshold ? '#2E7D5B' : '#C03A3A'
  const bg = v >= threshold ? '#D4E9DF' : '#F2DCDC'
  return `<span style="display:inline-block;padding:2px 10px;border-radius:20px;font-size:12px;font-weight:700;color:${color};background:${bg};">${v.toFixed(1)}%</span>`
}

function renderPercentBadgeInverse(value, threshold) {
  if (value == null) return '-'
  const v = Number(value)
  const color = v <= threshold ? '#2E7D5B' : '#C03A3A'
  const bg = v <= threshold ? '#D4E9DF' : '#F2DCDC'
  return `<span style="display:inline-block;padding:2px 10px;border-radius:20px;font-size:12px;font-weight:700;color:${color};background:${bg};">${v.toFixed(1)}%</span>`
}

function formatPercent(val) {
  if (val == null) return '-'
  return `${Number(val).toFixed(1)}%`
}

function formatNumber(val) {
  if (val == null) return '-'
  return Number(val).toLocaleString()
}

function kpiBadgeClass(val, threshold) {
  if (val == null) return ''
  return Number(val) >= threshold ? 'kpi-badge kpi-badge--success' : 'kpi-badge kpi-badge--danger'
}

function kpiBadgeClassInverse(val, threshold) {
  if (val == null) return ''
  return Number(val) <= threshold ? 'kpi-badge kpi-badge--success' : 'kpi-badge kpi-badge--danger'
}
</script>

<style scoped>
/* ========================================
   PartnerKpiTable
   ======================================== */
.dashboard-panel {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: var(--radius-xl, 16px);
  border: 1px solid var(--color-gray-100, #E8EAED);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.08));
  padding: 24px;
  transition: box-shadow var(--transition-normal, 0.2s ease), transform var(--transition-normal, 0.2s ease);
}

.dashboard-panel:hover {
  box-shadow: var(--shadow-md, 0 2px 6px rgba(0, 0, 0, 0.1));
  transform: translateY(-1px);
}

.dashboard-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-panel__title::before {
  content: '';
  width: 4px;
  height: 16px;
  background: var(--color-primary, #3B5998);
  border-radius: 2px;
  flex-shrink: 0;
}

/* ===== AG Grid Wrapper ===== */
.kpi-grid-wrapper {
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  border: 1px solid var(--color-gray-100, #E8EAED);
}

:deep(.kpi-ag-grid .ag-header) {
  background: var(--color-gray-50, #F2F3F5);
  border-bottom: 1px solid var(--color-gray-200, #B8BCC4);
}

:deep(.kpi-ag-grid .ag-header-cell-label) {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-gray-700, #2E333B);
}

:deep(.kpi-ag-grid .ag-row) {
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
}

:deep(.kpi-ag-grid .ag-row:hover) {
  background: var(--color-gray-50, #F2F3F5);
}

:deep(.kpi-ag-grid .ag-cell) {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--color-gray-700, #2E333B);
}

/* ===== Mobile Cards ===== */
.kpi-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.kpi-card {
  background: var(--color-white, #fff);
  border-radius: var(--m-card-radius, 12px);
  border: var(--m-card-border, 1px solid #e5e7eb);
  box-shadow: var(--m-card-shadow, 0 1px 4px rgba(0, 0, 0, 0.06));
  padding: 14px;
}

.kpi-card__header {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
}

.kpi-card__name {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-gray-800, #1E2228);
}

.kpi-card__metrics {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.kpi-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px;
  background: var(--color-gray-50, #F2F3F5);
  border-radius: var(--radius-md, 8px);
}

.kpi-metric__label {
  font-size: 10px;
  font-weight: 600;
  color: var(--color-gray-500, #5C616B);
  text-align: center;
}

.kpi-metric__value {
  font-size: 15px;
  font-weight: 700;
}

.kpi-metric__value--mono {
  font-variant-numeric: tabular-nums;
  color: var(--color-gray-800, #1E2228);
}

/* ===== KPI Badges ===== */
.kpi-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
}

.kpi-badge--success {
  color: var(--color-success, #2E7D5B);
  background: var(--color-success-light, #D4E9DF);
}

.kpi-badge--danger {
  color: var(--color-danger, #C03A3A);
  background: var(--color-danger-light, #F2DCDC);
}

/* ===== Empty State ===== */
.kpi-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  gap: 12px;
}

.kpi-empty__icon {
  opacity: 0.4;
}

.kpi-empty__text {
  font-size: 13px;
  color: var(--color-gray-500, #5C616B);
}

/* ===== Mobile ===== */
@media (max-width: 767px) {
  .dashboard-panel {
    padding: 16px;
    border-radius: var(--radius-lg, 12px);
  }
}
</style>
